import { ReactNode, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errDetail, imgSrc } from "@/lib/api";

export function ConfirmDelete({ title, onConfirm, children }: { title: string; onConfirm: () => void; children: ReactNode }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ImageUploadButton({ onUploaded, testId }: { onUploaded: (url: string) => void; testId: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setLoading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload", fd);
        onUploaded(data.url);
      } catch (err) {
        toast.error(`Error al subir ${f.name}: ${errDetail(err)}`);
      }
    }
    setLoading(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={loading} data-testid={testId}
        className="inline-flex items-center gap-2 rounded-md border border-dashed border-slate-400 px-4 py-2 text-sm text-slate-600 hover:border-[#00A8A0] hover:text-[#008C86] disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Subir foto
      </button>
    </>
  );
}

export function Thumb({ src, onRemove, testId }: { src: string; onRemove: () => void; testId: string }) {
  return (
    <div className="relative h-20 w-28 overflow-hidden rounded-md border border-slate-200">
      <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
      <button type="button" onClick={onRemove} data-testid={testId} className="absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}
